import BigNumber from 'bignumber.js'
import { HStack, Input, SimpleGrid, Text, VStack } from '@chakra-ui/react'
import { TokenMoney } from '@ninjablaze/sdk-ts'

import { StyledButton } from '@/components/atoms/AmountButtons/AmountButtons'

type GameZoneAmountInputProps = {
  value: string
  onChange: (value: string) => void
  denom: string
  currency: string
  precision: number
  maxAmount: BigNumber
  isDisabled?: boolean
  usdValue?: string
}

export const GameZoneAmountInput: React.FC<GameZoneAmountInputProps> = ({
  value,
  onChange,
  denom,
  currency,
  precision,
  maxAmount,
  isDisabled = false,
  usdValue,
}) => {
  const formatAmount = (amount: BigNumber.Value) =>
    new BigNumber(amount).decimalPlaces(precision, BigNumber.ROUND_DOWN).toFixed()

  const handleChange = (nextValue: string) => {
    const normalizedValue = nextValue.replace(',', '.')
    const amountPattern = new RegExp(`^\\d*(?:\\.\\d{0,${precision}})?$`)

    if (normalizedValue === '' || amountPattern.test(normalizedValue)) {
      onChange(normalizedValue)
    }
  }

  const quickAmounts = [0, 0.1, 0.25, 0.5, 1].map((part) => {
    const amount = formatAmount(maxAmount.multipliedBy(part))

    return {
      value: amount,
      label: part === 1 ? 'MAX' : TokenMoney.fromHuman(amount, denom).humanCompactFormat,
    }
  })

  return (
    <VStack align="stretch" spacing={1}>
      <HStack h="56px" px={{ base: 3, sm: 5 }} bg="ninja.500" borderRadius="12px" spacing={3}>
        <Input
          type="text"
          inputMode="decimal"
          value={value}
          placeholder="0"
          isDisabled={isDisabled}
          onChange={(event) => handleChange(event.target.value)}
          h="auto"
          minW={0}
          p={0}
          border={0}
          borderRadius={0}
          color="white"
          fontSize="16px"
          fontWeight={700}
          lineHeight={1}
          _placeholder={{ color: 'white' }}
          _focusVisible={{ boxShadow: 'none' }}
        />
        <Text flexShrink={0} fontSize="16px" fontWeight={700} color="ninja.200" lineHeight={1}>
          {currency}
        </Text>
        {usdValue && (
          <Text
            display={{ base: 'none', sm: 'block' }}
            flexShrink={0}
            fontSize="12px"
            fontWeight={700}
            color="ninja.200"
          >
            {usdValue}
          </Text>
        )}
      </HStack>
      <SimpleGrid columns={5} gap={1} w="full">
        {quickAmounts.map((quickAmount, index) => (
          <StyledButton
            key={index}
            w="full"
            px={2}
            overflow="hidden"
            textOverflow="ellipsis"
            color="ninja.100"
            bg={value !== '' && new BigNumber(value).eq(quickAmount.value) ? 'ninja.400' : 'ninja.500'}
            aria-pressed={value !== '' && new BigNumber(value).eq(quickAmount.value)}
            isDisabled={isDisabled || maxAmount.isZero()}
            onClick={() => onChange(quickAmount.value)}
          >
            {quickAmount.label}
          </StyledButton>
        ))}
      </SimpleGrid>
    </VStack>
  )
}
